import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Button } from '@depop/web-ui-kit/Button';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ExtendedLink } from '../ExtendedLink';

import { AppDownloadSection } from './AppDownloadSection';
import styles from './styles.module.css';

import { getLoginRedirectParams } from '@/modules/login/helpers';
import { useSignup } from '@/modules/signup/hooks/useSignup';
import { useSignupModalTracking } from '@/modules/signup/hooks/useSignupModalTracking';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const CLOUDFRONT_HOST = process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST;

export enum SignUpModalAction {
  APP_STORE = 'AppStore',
  LOGIN = 'Login',
  PLAY_STORE = 'PlayStore',
  SIGN_UP = 'Signup',
}

export type Props = {
  downloadAppTrackingId: string;
  followedUser?: string;
};

export function DownloadAppModal({
  followedUser,
  downloadAppTrackingId,
}: Props) {
  const t = useTranslations('common');
  const { url: signupUrl } = useSignup();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryString = searchParams.toString();
  const pathnameWithQuery = `${pathname}${
    queryString ? `?${queryString}` : ''
  }`;
  const [isAuthenticated] = useCurrentUser();
  const { sendSignupModalActionEvent } = useSignupModalTracking();

  return (
    <div className={styles.modalContainer}>
      <img
        src={`${CLOUDFRONT_HOST}/web/assets/images/depop-smile-sticker.png`}
        srcSet={`${CLOUDFRONT_HOST}/web/assets/images/depop-smile-sticker.png 251w, ${CLOUDFRONT_HOST}/web/assets/images/depop-smile-sticker@2x.png 501w`}
        alt="Depop"
        className={styles.depopSticker}
      />
      <Heading variant="md" className={styles.modalTitle}>
        {t('LogInModal.LoggedOutTitle')}
      </Heading>
      {!isAuthenticated && (
        <>
          <Body variant="sm-regular" className={styles.modalBodyText}>
            {followedUser
              ? t('LogInModal.FollowBody', { user: followedUser })
              : t('LogInModal.LoggedOutBody')}
          </Body>
          <ExtendedLink
            href={signupUrl}
            className={styles.buttonContainer}
            onClick={() =>
              sendSignupModalActionEvent(SignUpModalAction.SIGN_UP)
            }
          >
            <Button as="span" className={styles.signupButton} block>
              {t('Nav.SignUp')}
            </Button>
          </ExtendedLink>
          <ExtendedLink
            href={`/login/${getLoginRedirectParams(pathnameWithQuery)}`}
            className={styles.buttonContainer}
            onClick={() => sendSignupModalActionEvent(SignUpModalAction.LOGIN)}
          >
            <Button as="span" className={styles.loginButton} block outline>
              {t('LogInModal.LogIn')}
            </Button>
          </ExtendedLink>
          <div className={styles.horizontalBreakContainer}>
            <hr className={styles.horizontalBreak} />
            <Body variant="sm-regular" className={styles.orText}>
              {t('LogInModal.Or')}
            </Body>
            <hr className={styles.horizontalBreak} />
          </div>
        </>
      )}
      <AppDownloadSection downloadAppTrackingId={downloadAppTrackingId} />
    </div>
  );
}
